import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Slider,
  Tabs,
  Modal,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  PlusOutlined,
  LinkOutlined,
  TeamOutlined,
  DatabaseOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Influencer, InfluencerSearchParams, ScreeningTask, SimilarInfluencer } from '../../types/scout';
import type { AsyncTask } from '../../types/api';
import { scoutApi } from '../../services/scout';
import { ROUTES } from '../../config/routes';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

type SearchMode = 'database' | 'competitor' | 'similar';

export const InfluencerSearch: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Influencer[]>([]);
  const [similarData, setSimilarData] = useState<SimilarInfluencer[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchMode, setSearchMode] = useState<SearchMode>('database');
  const [databaseForm] = Form.useForm();
  const [competitorForm] = Form.useForm();
  const [similarForm] = Form.useForm();

  // 候选列表相关
  const [addToListModalVisible, setAddToListModalVisible] = useState(false);
  const [screeningTasks, setScreeningTasks] = useState<ScreeningTask[]>([]);
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [tasksLoading, setTasksLoading] = useState(false);

  // 加载筛选任务列表
  const loadScreeningTasks = async () => {
    setTasksLoading(true);
    try {
      const res = await scoutApi.getScreeningTasks({ page: 1, page_size: 100 });
      setScreeningTasks(res.data || []);
    } catch (error) {
      message.error('加载筛选任务列表失败');
    } finally {
      setTasksLoading(false);
    }
  };

  useEffect(() => {
    loadScreeningTasks();
  }, []);

  // 数据库搜索
  const handleDatabaseSearch = async (
    values: Record<string, unknown>,
    overridePagination?: { page: number; pageSize: number }
  ) => {
    setLoading(true);
    try {
      const followerRange = values.follower_range as [number, number] | undefined;
      const engagementRate = values.engagement_rate as [number, number] | undefined;
      const page = overridePagination?.page ?? pagination.current;
      const pageSize = overridePagination?.pageSize ?? pagination.pageSize;
      const params: InfluencerSearchParams = {
        keyword: values.keywords as string | undefined,
        platforms: values.platforms as string[] | undefined,
        countries: values.countries as string[] | undefined,
        categories: values.categories as string[] | undefined,
        follower_min: followerRange?.[0],
        follower_max: followerRange?.[1],
        engagement_rate_min: engagementRate?.[0],
        engagement_rate_max: engagementRate?.[1],
        page: page,
        page_size: pageSize,
      };
      const res = await scoutApi.searchInfluencers(params);
      setData(res.data || []);
      setPagination((prev) => ({
        ...prev,
        total: res.pagination?.total || 0,
      }));
    } catch (error) {
      message.error('搜索失败');
    } finally {
      setLoading(false);
    }
  };

  // 竞品采集
  const handleCompetitorSearch = async (values: { competitor_url: string }) => {
    setLoading(true);
    try {
      const res = await scoutApi.searchCompetitors({ competitor_url: values.competitor_url });
      message.success(`竞品采集任务已创建，任务ID: ${res.data.task_id}`);
      // 竞品采集是异步任务，这里可以跳转到任务列表或显示提示
    } catch (error) {
      message.error('竞品采集失败');
    } finally {
      setLoading(false);
    }
  };

  // 相似推荐
  const handleSimilarSearch = async (values: { influencer_url: string }) => {
    setLoading(true);
    try {
      const res = await scoutApi.searchSimilar({ influencer_url: values.influencer_url });
      // 将相似红人数据转换为Influencer格式用于展示
      const similarInfluencers = res.data.similar_influencers || [];
      setSimilarData(similarInfluencers);
      // 转换格式用于表格展示
      const convertedData: Influencer[] = similarInfluencers.map((item) => ({
        id: item.id,
        name: item.name,
        channel_url: '',
        platform: item.platform as 'youtube' | 'tiktok' | 'instagram' | 'other',
        follower_count: item.follower_count,
        country: '-',
        engagement_rate: undefined,
        content_categories: [item.match_reason],
      }));
      setData(convertedData);
      setPagination({
        ...pagination,
        total: convertedData.length,
      });
    } catch (error) {
      message.error('相似推荐搜索失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    const newCurrent = newPagination.current || 1;
    const newPageSize = newPagination.pageSize || 10;
    setPagination((prev) => ({
      ...prev,
      current: newCurrent,
      pageSize: newPageSize,
    }));
    // 如果是数据库搜索模式，直接传入新分页参数，不依赖异步 state 更新
    if (searchMode === 'database') {
      const values = databaseForm.getFieldsValue();
      handleDatabaseSearch(values, { page: newCurrent, pageSize: newPageSize });
    }
  };

  // 打开添加到列表弹窗
  const handleOpenAddToList = (record: Influencer) => {
    setSelectedInfluencer(record);
    setAddToListModalVisible(true);
  };

  // 确认添加到列表
  const handleConfirmAddToList = async () => {
    if (!selectedInfluencer || !selectedTaskId) {
      message.warning('请选择筛选任务');
      return;
    }
    try {
      await scoutApi.addCandidate(
        selectedTaskId,
        selectedInfluencer.id,
        undefined,
        searchMode,
        `从${searchMode === 'database' ? '数据库搜索' : searchMode === 'competitor' ? '竞品采集' : '相似推荐'}添加`
      );
      message.success('已添加到筛选任务');
      setAddToListModalVisible(false);
      setSelectedInfluencer(null);
      setSelectedTaskId('');
    } catch (error) {
      message.error('添加失败');
    }
  };

  // 获取合作状态标签
  const getCooperationStatusTag = (record: Influencer) => {
    if (record.is_cooperated) {
      return <Tag color="green">已合作</Tag>;
    }
    return <Tag>未合作</Tag>;
  };

  const columns = [
    {
      title: '红人名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Influencer) => (
        <a onClick={() => navigate(`/scout/influencers/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string) => <Tag>{platform?.toUpperCase?.() || platform}</Tag>,
    },
    {
      title: '粉丝数',
      dataIndex: 'follower_count',
      key: 'follower_count',
      render: (count: number) => count?.toLocaleString() || '-',
    },
    {
      title: '互动率',
      dataIndex: 'engagement_rate',
      key: 'engagement_rate',
      render: (rate: number) => rate ? `${(rate * 100).toFixed(2)}%` : '-',
    },
    {
      title: '国家',
      dataIndex: 'country',
      key: 'country',
      render: (country: string) => country || '-',
    },
    {
      title: '合作状态',
      key: 'cooperation_status',
      render: (_: unknown, record: Influencer) => getCooperationStatusTag(record),
    },
    {
      title: '内容分类',
      dataIndex: 'content_categories',
      key: 'content_categories',
      render: (categories: string[]) => (
        <>
          {categories?.slice(0, 2).map((cat) => (
            <Tag key={cat}>{cat}</Tag>
          ))}
          {categories?.length > 2 && <Tag>+{categories.length - 2}</Tag>}
        </>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: unknown, record: Influencer) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/scout/influencers/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="加入候选列表">
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => handleOpenAddToList(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 数据库搜索表单
  const renderDatabaseSearchForm = () => (
    <Form form={databaseForm} layout="vertical" onFinish={handleDatabaseSearch}>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="keywords" label="关键词">
            <Input placeholder="搜索红人名称、标签等" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="platforms" label="平台">
            <Select mode="multiple" placeholder="选择平台">
              <Option value="youtube">YouTube</Option>
              <Option value="tiktok">TikTok</Option>
              <Option value="instagram">Instagram</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="countries" label="国家">
            <Select mode="multiple" placeholder="选择国家">
              <Option value="US">美国</Option>
              <Option value="UK">英国</Option>
              <Option value="DE">德国</Option>
              <Option value="FR">法国</Option>
              <Option value="JP">日本</Option>
              <Option value="KR">韩国</Option>
              <Option value="CN">中国</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="categories" label="内容分类">
            <Select mode="multiple" placeholder="选择分类">
              <Option value="科技">科技</Option>
              <Option value="美妆">美妆</Option>
              <Option value="时尚">时尚</Option>
              <Option value="游戏">游戏</Option>
              <Option value="美食">美食</Option>
              <Option value="旅行">旅行</Option>
              <Option value="健身">健身</Option>
              <Option value="教育">教育</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="follower_range" label="粉丝数范围">
            <Slider
              range
              min={0}
              max={10000000}
              step={10000}
              tooltip={{ formatter: (value: number | undefined) => value?.toLocaleString() }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="engagement_rate" label="互动率范围">
            <Slider
              range
              min={0}
              max={0.2}
              step={0.01}
              tooltip={{ formatter: (value: number | undefined) => `${((value || 0) * 100).toFixed(0)}%` }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
          搜索
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={() => databaseForm.resetFields()}>
          重置
        </Button>
      </Form.Item>
    </Form>
  );

  // 竞品采集表单
  const renderCompetitorSearchForm = () => (
    <Form form={competitorForm} layout="vertical" onFinish={handleCompetitorSearch}>
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            name="competitor_url"
            label="竞品频道/账号链接"
            rules={[{ required: true, message: '请输入竞品链接' }]}
          >
            <Input
              placeholder="输入竞品YouTube频道、TikTok账号或Instagram主页链接"
              prefix={<LinkOutlined />}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <Button type="primary" htmlType="submit" icon={<TeamOutlined />} loading={loading}>
          采集竞品合作红人
        </Button>
      </Form.Item>
      <Text type="secondary">
        系统将分析该竞品的合作红人，并推荐可能适合您品牌的红人资源。
      </Text>
    </Form>
  );

  // 相似推荐表单
  const renderSimilarSearchForm = () => (
    <Form form={similarForm} layout="vertical" onFinish={handleSimilarSearch}>
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            name="influencer_url"
            label="种子红人链接"
            rules={[{ required: true, message: '请输入红人链接' }]}
          >
            <Input
              placeholder="输入您喜欢的红人YouTube频道、TikTok账号或Instagram主页链接"
              prefix={<LinkOutlined />}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
          查找相似红人
        </Button>
      </Form.Item>
      <Text type="secondary">
        系统将基于该红人的特征，推荐风格相似、受众重叠的红人资源。
      </Text>
    </Form>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        红人搜索
      </Title>

      {/* 搜索条件 */}
      <Card style={{ marginBottom: 24 }}>
        <Tabs activeKey={searchMode} onChange={(key) => setSearchMode(key as SearchMode)}>
          <TabPane
            tab={<span><DatabaseOutlined /> 数据库搜索</span>}
            key="database"
          >
            {renderDatabaseSearchForm()}
          </TabPane>
          <TabPane
            tab={<span><TeamOutlined /> 竞品采集</span>}
            key="competitor"
          >
            {renderCompetitorSearchForm()}
          </TabPane>
          <TabPane
            tab={<span><SearchOutlined /> 相似推荐</span>}
            key="similar"
          >
            {renderSimilarSearchForm()}
          </TabPane>
        </Tabs>
      </Card>

      {/* 搜索结果 */}
      <Card title={`搜索结果 (${pagination.total})`}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
          }}
          locale={{ emptyText: searchMode === 'database' ? '请输入搜索条件进行搜索' : '请输入链接开始搜索' }}
        />
      </Card>

      {/* 添加到候选列表弹窗 */}
      <Modal
        title="添加到筛选任务"
        open={addToListModalVisible}
        onOk={handleConfirmAddToList}
        onCancel={() => {
          setAddToListModalVisible(false);
          setSelectedInfluencer(null);
          setSelectedTaskId('');
        }}
        confirmLoading={loading}
      >
        <p>将 <Text strong>{selectedInfluencer?.name}</Text> 添加到以下筛选任务：</p>
        <Select
          style={{ width: '100%' }}
          placeholder="选择筛选任务"
          loading={tasksLoading}
          value={selectedTaskId || undefined}
          onChange={(value) => setSelectedTaskId(value)}
          options={screeningTasks.map((task) => ({
            label: task.name,
            value: task.id,
          }))}
        />
        {screeningTasks.length === 0 && !tasksLoading && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            暂无筛选任务，请先创建筛选任务
          </Text>
        )}
      </Modal>
    </div>
  );
};
